import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AxiosResponse } from 'axios';
import {
  Autocomplete,
  CircularProgress,
  Popper,
  TextField,
  debounce,
  Typography,
  colors,
  type PopperProps,
} from '@mui/material';
import { Search } from '@mui/icons-material';

import { IGetSearch } from 'apis/type';
import useClassSection from 'store/section/useClassSection';
import usePatient from 'store/patient/usePatient';

interface Props {
  listKey: string;
  valueKey: string;
  variant?: 'outlined' | 'standard';
  noOptionsText?: string;
  helperText?: string;
  placeholder?: string;
  onChange: (value: any) => void;
  getOptionLabel: (option: any) => string;
  renderOption?: (props: any, option: any) => React.ReactNode;
  getApi: (request: IGetSearch) => Promise<AxiosResponse<any>>;
  isDisabled?: boolean;
  options?: any[];
  syncWithPatient?: boolean;
  /** 마운트 시 목록을 불러와 첫 항목을 기본 선택 (환자 미선택일 때만) */
  autoSelectFirst?: boolean;
  /** 분반 검색 — Redux class_section과 동기화·표시 (기본 -1 = 전체 학생) */
  syncWithClassSection?: boolean;
  /** 있으면 드롭다운 패널 너비·좌측을 이 요소에 맞춤 (교수 메인 3칸 검색) */
  matchDropdownWidthRef?: React.RefObject<HTMLElement | null>;
}

function MatchToolbarWidthPopper({
  matchDropdownWidthRef,
  ...props
}: PopperProps & {
  matchDropdownWidthRef: React.RefObject<HTMLElement | null>;
}) {
  const { style, modifiers, ...other } = props;
  const width =
    matchDropdownWidthRef.current?.getBoundingClientRect().width ??
    style?.width;

  return (
    <Popper
      {...other}
      style={{ ...style, width }}
      placement="bottom-start"
      modifiers={
        [
          ...(Array.isArray(modifiers) ? modifiers : []),
          {
            name: 'offset',
            options: {
              offset: ({ reference }: { reference: { x: number } }) => {
                const el = matchDropdownWidthRef.current;
                if (!el) return [0, 1];
                return [el.getBoundingClientRect().left - reference.x, 1];
              },
            },
          },
          {
            name: 'matchToolbarWidth',
            enabled: true,
            phase: 'beforeWrite',
            requires: ['computeStyles'],
            fn: ({ state }: { state: any }) => {
              const el = matchDropdownWidthRef.current;
              if (!el) return;
              state.styles.popper.width = `${el.getBoundingClientRect().width}px`;
            },
          },
        ] as PopperProps['modifiers']
      }
    />
  );
}

const MuiAutocomplete = (props: Props) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedValue, setSelectedValue] = useState<any>(null);
  const autoSelectFirstAppliedRef = useRef(false);
  const classSectionDefaultAppliedRef = useRef(false);
  const { class_section_id, displayName: classSectionDisplayName } =
    useClassSection();
  const { patient } = usePatient();
  const {
    listKey,
    valueKey,
    variant = 'outlined',
    noOptionsText,
    helperText,
    placeholder,
    onChange,
    renderOption,
    getOptionLabel,
    getApi,
    isDisabled,
    syncWithPatient = false,
    autoSelectFirst = false,
    syncWithClassSection = false,
    matchDropdownWidthRef,
  } = props;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const applyDefaultSelection = useCallback(
    (processedOptions: any[]) => {
      if (!processedOptions.length) return;

      if (autoSelectFirst && !autoSelectFirstAppliedRef.current) {
        if (syncWithPatient && patient) {
          autoSelectFirstAppliedRef.current = true;
        } else if (!patient) {
          const first = processedOptions[0];
          autoSelectFirstAppliedRef.current = true;
          setSelectedValue(first);
          onChangeRef.current(first);
        }
      }

      if (syncWithClassSection && !classSectionDefaultAppliedRef.current) {
        const matched =
          processedOptions.find(
            (option: any) => option?.[valueKey] === class_section_id
          ) ??
          processedOptions.find((option: any) => option?.[valueKey] === -1);

        if (matched) {
          classSectionDefaultAppliedRef.current = true;
          setSelectedValue(matched);
          const needsReduxSync =
            matched[valueKey] !== class_section_id ||
            (class_section_id === -1 && !classSectionDisplayName);
          if (needsReduxSync) {
            onChangeRef.current(matched);
          }
        }
      }
    },
    [
      autoSelectFirst,
      classSectionDisplayName,
      class_section_id,
      patient,
      syncWithClassSection,
      syncWithPatient,
      valueKey,
    ]
  );

  const onChangeOptions = useCallback(
    (keyword: string) => {
      setIsLoading(true);
      getApi({ page: 1, keyword })
        .then(({ data }) => {
          const nextOptions = data?.[listKey] || [];
          setTotalCount(data?.count);
          setOptions(nextOptions);
          applyDefaultSelection(nextOptions);
        })
        .catch(error => {
          console.error('Failed to fetch options:', error);
          setOptions([]);
          setTotalCount(0);
        })
        .finally(() => setIsLoading(false));
    },
    // eslint-disable-next-line
    [class_section_id, patient, listKey, applyDefaultSelection]
  );

  useEffect(() => {
    if (open) {
      onChangeOptions('');
    }
  }, [onChangeOptions, open]);

  useEffect(() => {
    if (!autoSelectFirst && !syncWithClassSection) return;
    onChangeOptions('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectFirst, syncWithClassSection]);

  useEffect(() => {
    if (syncWithPatient && !patient) {
      autoSelectFirstAppliedRef.current = false;
    }
  }, [patient, syncWithPatient]);

  useEffect(() => {
    if (!syncWithClassSection) return;
    if (class_section_id === -1 && !classSectionDisplayName) {
      classSectionDefaultAppliedRef.current = false;
      if (options.length > 0) {
        applyDefaultSelection(options);
      }
    }
  }, [
    applyDefaultSelection,
    classSectionDisplayName,
    class_section_id,
    options,
    syncWithClassSection,
  ]);

  useEffect(() => {
    if (!syncWithPatient) return;
    setSelectedValue(patient ?? null);
  }, [patient, syncWithPatient]);

  useEffect(() => {
    if (!syncWithClassSection || options.length === 0) return;
    const matched =
      options.find((option: any) => option?.[valueKey] === class_section_id) ??
      (class_section_id === -1
        ? options.find((option: any) => option?.[valueKey] === -1)
        : undefined);
    if (matched) {
      setSelectedValue(matched);
    }
  }, [class_section_id, options, syncWithClassSection, valueKey]);

  const isOptionEqualToValue = (option: any, value: any) =>
    option?.[valueKey] === value?.[valueKey];

  const onChangeTextField = debounce(e => {
    const keyword = e.target.value;
    onChangeOptions(keyword);
  }, 500);

  const handleChange = (_: any, value: any) => {
    setSelectedValue(value);
    onChange(value);
    if (value === null) {
      onChangeOptions('');
    }
  };

  const TextFieldEndAdornment = ({
    endAdornment,
  }: {
    endAdornment: React.ReactNode;
  }) => (
    <Fragment>
      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
      {endAdornment}
    </Fragment>
  );

  const ToolbarMatchPopper = useMemo(() => {
    if (!matchDropdownWidthRef) return undefined;
    return function ToolbarMatchPopperInner(popperProps: PopperProps) {
      return (
        <MatchToolbarWidthPopper
          {...popperProps}
          matchDropdownWidthRef={matchDropdownWidthRef}
        />
      );
    };
  }, [matchDropdownWidthRef]);

  return (
    <Autocomplete
      fullWidth
      open={open}
      value={selectedValue}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={isOptionEqualToValue}
      PopperComponent={ToolbarMatchPopper}
      componentsProps={
        matchDropdownWidthRef
          ? {
              paper: {
                sx: {
                  mt: 0,
                  overflow: 'hidden',
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  width: '100%',
                },
              },
            }
          : undefined
      }
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      noOptionsText={<Typography variant="caption">{noOptionsText}</Typography>}
      options={options}
      loading={isLoading}
      disabled={isDisabled}
      onChange={handleChange}
      renderInput={params => (
        <TextField
          {...params}
          variant={variant}
          helperText={helperText}
          placeholder={placeholder}
          onChange={onChangeTextField}
          InputProps={{
            ...params.InputProps,
            startAdornment: totalCount ? (
              <Typography
                variant="caption"
                color={colors.grey[500]}
                sx={{ mr: 1 }}
              >
                (총 {totalCount}명)
              </Typography>
            ) : (
              <Search color="disabled" sx={{ fontSize: 16, mr: 1 }} />
            ),
            endAdornment: (
              <TextFieldEndAdornment
                endAdornment={params.InputProps.endAdornment}
              />
            ),
            sx: { fontSize: 14 },
          }}
        />
      )}
    />
  );
};

export default MuiAutocomplete;
